import { computed, inject, Injectable, signal } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BREAKPOINTS,
  DeviceSize,
  Orientation,
  ORIENTATION_QUERIES,
  IResponsiveState,
} from '../domain/responsive.model';

@Injectable({
  providedIn: 'root',
})
export class ResponsiveService {
  private readonly breakpointObserver = inject(BreakpointObserver);

  private readonly initialState: IResponsiveState = {
    deviceSize: DeviceSize.MEDIUM,
    orientation: Orientation.PORTRAIT,
  };

  private readonly _responsiveState = signal<IResponsiveState>(this.initialState);
  public readonly deviceSize = signal<DeviceSize>(this.initialState.deviceSize);
  public readonly orientation = signal<Orientation>(this.initialState.orientation);
  public readonly state = this._responsiveState.asReadonly();

  public readonly isXSmall = computed(() => this.deviceSize() === DeviceSize.XSMALL);
  public readonly isSmall = computed(() => this.deviceSize() === DeviceSize.SMALL);
  public readonly isMedium = computed(() => this.deviceSize() === DeviceSize.MEDIUM);
  public readonly isLarge = computed(() => this.deviceSize() === DeviceSize.LARGE);
  public readonly isXLarge = computed(() => this.deviceSize() === DeviceSize.XLARGE);

  public readonly isMobile = computed(
    // eslint-disable-next-line prettier/prettier
    () =>
      this.deviceSize() === DeviceSize.XSMALL ||
      this.deviceSize() === DeviceSize.SMALL,
  );

  public readonly isDesktop = computed(
    () =>
      this.deviceSize() === DeviceSize.MEDIUM ||
      this.deviceSize() === DeviceSize.LARGE ||
      this.deviceSize() === DeviceSize.XLARGE,
  );

  public readonly isPortrait = computed(() => this.orientation() === Orientation.PORTRAIT);
  public readonly isLandscape = computed(() => this.orientation() === Orientation.LANDSCAPE);

  constructor() {
    this.initBreakpointObserver();
  }

  private initBreakpointObserver(): void {
    const breakpointQueries = [...Object.values(BREAKPOINTS), ...Object.values(ORIENTATION_QUERIES)];

    this.breakpointObserver
      .observe(breakpointQueries)
      .pipe(
        takeUntilDestroyed(),
        map((result) => {
          let deviceSize = DeviceSize.MEDIUM;
          if (result.breakpoints[BREAKPOINTS[DeviceSize.XSMALL]]) {
            deviceSize = DeviceSize.XSMALL;
          } else if (result.breakpoints[BREAKPOINTS[DeviceSize.SMALL]]) {
            deviceSize = DeviceSize.SMALL;
          } else if (result.breakpoints[BREAKPOINTS[DeviceSize.MEDIUM]]) {
            deviceSize = DeviceSize.MEDIUM;
          } else if (result.breakpoints[BREAKPOINTS[DeviceSize.LARGE]]) {
            deviceSize = DeviceSize.LARGE;
          } else if (result.breakpoints[BREAKPOINTS[DeviceSize.XLARGE]]) {
            deviceSize = DeviceSize.XLARGE;
          }

          let orientation = Orientation.PORTRAIT;
          if (result.breakpoints[ORIENTATION_QUERIES[Orientation.LANDSCAPE]]) {
            orientation = Orientation.LANDSCAPE;
          }

          return { deviceSize, orientation } as IResponsiveState;
        }),
      )
      .subscribe((state) => {
        this._responsiveState.set(state);
        this.deviceSize.set(state.deviceSize);
        this.orientation.set(state.orientation);
      });
  }

  public matchesBreakpoint(breakpoint: string): boolean {
    return this.breakpointObserver.isMatched(breakpoint);
  }
}
